import React, { useRef, useEffect, useState, useCallback }  from "react";
import ProductSection from "../../components/Product/ProductSection";
import CategoryTab from "../../components/CategoryTab/CategoryTab"
import instance from "../../services/services";
import { BRAND_EXCLUDE_CATEGORY_LIST_URL } from "../../utils/urls";
import useUserStore from "../../store/userStore";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../../hooks/useLanguage";
import PaginationSection from "../../components/Product/Pagination";
import DefaultSelect from '../More/DefaultSelect';

function CinemaPage(){
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useUserStore((state) => state.userInfo);
  const searchParams = new URLSearchParams(location.search);
  const category_code = searchParams.get('category_code');
  const title = searchParams.get('title');
  const [bestProducts, setBestProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryNames, setCategoryNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const pageRef = useRef();
  const [categoryInfo, setCategoryInfo] = useState([]);
  // isMobile 초기값을 즉시 계산하도록 수정
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  
  
  // 기본값 설정
  const defaultValue = title === "PURPOSE" ? "popular" : "regist_date";
  
  // 각 카테고리별 정렬 옵션 상태 추가
  const [categorySortOptions, setCategorySortOptions] = useState({});
  
  // 각 카테고리별 페이지네이션 상태 관리
  const [categoryPages, setCategoryPages] = useState({});
  
  // itemsPerPage를 동적으로 계산하는 함수를 useCallback으로 감싸기
  const getItemsPerPage = useCallback(() => {
    return isMobile ? 4 : 10;
  }, [isMobile]);

  // 각 카테고리별 정렬 변경 핸들러
  const handleCategorySortChange = (categoryId, value) => {
    
    // 해당 카테고리의 정렬 옵션만 업데이트
    setCategorySortOptions(prev => ({
      ...prev,
      [categoryId]: value
    }));
    
    // 해당 카테고리 데이터만 다시 불러오기
    fetchCategoryDataWithSort(categoryId, value);
  };

  // 카테고리별 정렬 데이터 불러오기 함수
  const fetchCategoryDataWithSort = async (categoryId, sortValue) => {
    try {
      const requestBody = {
        category_code: categoryId,
        member_seq: userInfo.member_seq ? userInfo.member_seq : '',
        searchSort: sortValue,
      };
      
      // 모바일일 때와 PC일 때 분기
      if (isMobile) {
        requestBody.mobileChk = "Y";
      } else {
        requestBody.offset = 1;
        requestBody.limit = getItemsPerPage();
      }
      
      const response = await instance.post(BRAND_EXCLUDE_CATEGORY_LIST_URL, requestBody);
      
      const categoryData = response.data.data.category || {};
      
      if (categoryData[categoryId]) {
        setCategories(prev => prev.map(category => 
          category.id === categoryId 
            ? { ...category, ...categoryData[categoryId] }
            : category
        ));
        
        // 페이지도 1로 리셋
        setCategoryPages(prev => ({
          ...prev,
          [categoryId]: 1
        }));
      }
    } catch (error) {
      console.error('Error fetching category data with sort:', error);
    }
  };

  const getMobileSwiperOptions = (sectionId) => ({
    slidesPerView: 2,
    spaceBetween: 8,
    autoHeight: false,
    slidesPerGroup: 2,
    modules: [
      require('swiper/modules').Pagination, 
      require('swiper/modules').Grid,
      require('swiper/modules').Navigation
    ],
    pagination: { 
      clickable: true,
      // dynamicBullets: true
    },
    navigation: {
      nextEl: `#${sectionId}-next`,
      prevEl: `#${sectionId}-prev`,
    },
    loop: false,
    centeredSlides: false,
    allowTouchMove: true,
    breakpoints: {
      480: {
        slidesPerView: 2,
        spaceBetween: 8,
        slidesPerGroup: 2,
      }
    }
  });

  // PC용 스와이퍼 옵션 수정
  const getPCSwiperOptions = (sectionId) => ({
    slidesPerView: 5, // 5개로 변경
    spaceBetween: 16,
    autoHeight: false,
    slidesPerGroup: 5, // 5개로 변경
    modules: [
      require('swiper/modules').Pagination, 
      require('swiper/modules').Grid,
      require('swiper/modules').Navigation
    ],
    pagination: { 
      clickable: true,
      // dynamicBullets: true
    },
    navigation: {
      nextEl: `#${sectionId}-next`,
      prevEl: `#${sectionId}-prev`,
    },
    loop: false,
    centeredSlides: false,
    allowTouchMove: true,
    breakpoints: {
      1280: {
        slidesPerView: 5, // 5개로 변경
        spaceBetween: 16,
        slidesPerGroup: 5, // 5개로 변경
      },
      1024: {
        slidesPerView: 4,
        spaceBetween: 12,
        slidesPerGroup: 4,
      },
      768: {
        slidesPerView: 3,
        spaceBetween: 8,
        slidesPerGroup: 3,
      }
    }
  });

  // 리사이즈 이벤트는 그대로 유지하되, 초기 데이터 로드와 분리
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  // 각 카테고리별 데이터를 가져오는 함수 수정
  const fetchCategoryData = useCallback(async (categoryId, page = 1, isPageClick = false) => {
    try {
      if (!isPageClick) {
        setLoading(true);
      }
      
      const currentSortOption = categorySortOptions[categoryId] || defaultValue;
      
      const requestBody = {
        category_code: categoryId,
        member_seq: userInfo.member_seq ? userInfo.member_seq : '',
        searchSort: currentSortOption,
      };
      
      // 모바일일 때와 PC일 때 분기
      if (isMobile) {
        requestBody.mobileChk = "Y";
      } else {
        requestBody.offset = page;
        requestBody.limit = getItemsPerPage();
        
        if (isPageClick) {
          requestBody.type = 'detail';
        }
      }
      
      const response = await instance.post(BRAND_EXCLUDE_CATEGORY_LIST_URL, requestBody);
      const categoryData = response.data.data.category || {};
      
      if (categoryData[categoryId]) {
        setCategories(prev => prev.map(category => 
          category.id === categoryId 
            ? { ...category, ...categoryData[categoryId] }
            : category
        ));
      }
    } catch (error) {
      console.error('Error fetching category data:', error);
    } finally {
      if (!isPageClick) {
        setLoading(false);
      }
    }
  }, [userInfo.member_seq, categorySortOptions, defaultValue, getItemsPerPage, isMobile]);

  // 초기 데이터 로드 - getItemsPerPage 의존성 제거
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const requestBody = {
          category_code: category_code,
          member_seq: userInfo.member_seq ? userInfo.member_seq : '',
          searchSort: defaultValue,
        };
        
        // 모바일일 때와 PC일 때 분기
        if (isMobile) {
          requestBody.mobileChk = "Y";
        } else {
          const currentLimit = 10;
          requestBody.offset = 1;
          requestBody.limit = currentLimit;
        }
        
        const response = await instance.post(BRAND_EXCLUDE_CATEGORY_LIST_URL, requestBody);
        
        setCategoryInfo(response.data.data.categoryInfo);
        setBestProducts(response.data.data.bestGoods.data || []);

        const categoryData = response.data.data.category || {};
        const categoriesArray = Object.keys(categoryData)
          .filter(key => key !== '0002' && /^\d+$/.test(key))
          .map(key => ({
            id: key,
            category_code: key,
            ...categoryData[key]
          }))
          .filter(category => category && category.category_name);

        setCategories(categoriesArray);
        
        const namesWithCodes = categoriesArray.map(cat => ({
          category_name: cat.category_name,
          category_code: cat.category_code
        }));
        setCategoryNames(namesWithCodes);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category_code, userInfo.member_seq, defaultValue, isMobile]); // isMobile만 의존성으로 유지

  // isMobile 변경 감지 useEffect 제거 (초기 데이터 로드에서 처리)

  // 카테고리별 페이지 변경 핸들러
  // const handleCategoryPageChange = (categoryId, page) => {
  //   setCategoryPages(prev => ({
  //     ...prev,
  //     [categoryId]: page
  //   }));
  //   // 페이지 클릭이므로 isPageClick을 true로 전달
  //   fetchCategoryData(categoryId, page, true);
  // };

  // // 카테고리별 화살표 클릭 핸들러
  // const handleCategoryArrowClick = (categoryId, page) => {
  //   setCategoryPages(prev => ({
  //     ...prev,
  //     [categoryId]: page
  //   }));
  //   // 화살표 클릭도 페이지 클릭과 동일하게 처리
  //   fetchCategoryData(categoryId, page, true);
  // };
  

  // 카테고리별 BeforeContent 컴포넌트 수정
  const CategoryBeforeContent = ({ cntRow, category }) => {
    // 해당 카테고리의 현재 정렬 옵션 가져오기 (없으면 기본값)
    const currentCategorySortOption = categorySortOptions[category.id] || defaultValue;
    
    return (
      <div className="auth_find" style={{marginBottom:"-16px", padding:0}}>
        <div className="product_total" style={{paddingLeft:"16px"}}>
          {t('product_total_count', {count: cntRow}) }
        </div>
        {title !== "BRAND" && title !== "PURPOSE" && (
          <div 
            className="more_btn" 
            style={{ cursor: 'pointer' }}
            onClick={() => {
              // 쿼리 파라미터 생성
              const queryParams = new URLSearchParams();
              queryParams.set('title', category.category_name);
              queryParams.set('mainCategory', title || '');
              queryParams.set('category_code', category.category_code);
              
              // cinemaPageSec 페이지로 이동하면서 필요한 모든 데이터를 함께 전달
              navigate(`/category/cinemaPageSec?${queryParams.toString()}`, {
                state: { 
                  mainCategories: categoryNames,
                  subCategories: [category],
                  selectedCategory: category_code,
                  selectedProducts: category.data || []
                }
              });
            }}
          >
            {t('product_more_view')}
          </div>
          )}
        {title === "PURPOSE" && (
          <DefaultSelect 
            defaultValue={currentCategorySortOption}
            onChange={(value) => handleCategorySortChange(category.id, value)}
          />
        )}
      </div>
    );
  };

  const handleBreadcrumbClick = (level) => {
    switch(level) {
      case 'home':
        navigate('/');
        break;
      default:
        // 기본 동작 (아무것도 하지 않음)
        break;
    }
  };

  // 모바일 스크롤 페이지네이션을 위한 상태 추가
  const [isScrolling, setIsScrolling] = useState({});

  // goToPage 함수를 useCallback으로 먼저 정의
  const goToPage = useCallback((categoryId, page) => {
    // const currentCategory = categories.find(cat => cat.id === categoryId);
    
    setIsScrolling(prev => ({ ...prev, [categoryId]: true }));
    setCategoryPages(prev => ({ ...prev, [categoryId]: page }));
    
    fetchCategoryData(categoryId, page, true).finally(() => {
      setTimeout(() => {
        setIsScrolling(prev => ({ ...prev, [categoryId]: false }));
      }, 200);
    });
  }, [fetchCategoryData]);

  // 터치 시작 위치 저장용 ref
  const touchStartRef = useRef({});

  // 터치 시작 핸들러
  const handleTouchStart = useCallback((e, categoryId) => {
    if (!isMobile) return;
    
    const touch = e.touches[0];
    touchStartRef.current[categoryId] = {
      x: touch.clientX,
      time: Date.now()
    };
  }, [isMobile]);

  // 터치 종료 핸들러 (드래그 방향 감지)
  const handleTouchEnd = useCallback((e, categoryId) => {
    if (!isMobile || isScrolling[categoryId]) return;
    
    const touchStart = touchStartRef.current[categoryId];
    if (!touchStart) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaTime = Date.now() - touchStart.time;
    
    // 최소 드래그 거리와 시간 체크
    if (Math.abs(deltaX) < 50 || deltaTime > 500) return;

    const currentCategory = categories.find(cat => cat.id === categoryId);
    if (!currentCategory) return;

    const itemsPerPage = 4;
    const totalPages = Math.ceil(currentCategory.cntRow / itemsPerPage);
    const currentPage = categoryPages[categoryId] || 1;

    // 드래그 방향에 따른 페이지 이동
    if (deltaX > 0 && currentPage > 1) {
      // 오른쪽 드래그 = 이전 페이지
      goToPage(categoryId, currentPage - 1);
    } else if (deltaX < 0 && currentPage < totalPages) {
      // 왼쪽 드래그 = 다음 페이지
      goToPage(categoryId, currentPage + 1);
    }

    // 터치 데이터 정리
    delete touchStartRef.current[categoryId];
  }, [isMobile, isScrolling, categories, categoryPages, goToPage]);

  // ProductSection에 스크롤 이벤트를 추가하는 useEffect 수정
  useEffect(() => {
    if (!isMobile || categories.length === 0) return;
    
    const scrollHandlers = [];
    
    // DOM이 완전히 렌더링될 때까지 대기
    const timer = setTimeout(() => {
      categories.forEach(category => {
        const productContainer = document.querySelector(
          `[data-category-id="${category.id}"] .products-grid`
        );
        
        if (productContainer) {
          
          // CSS에서 이미 스타일이 적용되므로 JavaScript에서는 스크롤 이벤트만 처리
          // 마지막 페이지 아이템이 2개 미만일 때도 2열로 유지하도록 추가 스타일 적용
          const productItems = productContainer.querySelectorAll('.product-item');
          const totalItems = productItems.length;
          if (totalItems > 0) {
            // 마지막 페이지의 아이템 수 계산
            const itemsPerPage = 4;
            const currentPage = categoryPages[category.id] || 1;
            const itemsInCurrentPage = Math.min(itemsPerPage, totalItems - (currentPage - 1) * itemsPerPage);
            
            // 기존 플레이스홀더 제거
            const existingPlaceholders = productContainer.querySelectorAll('.grid-placeholder');
            existingPlaceholders.forEach(placeholder => placeholder.remove());
            
            // 마지막 페이지이고 아이템이 4개 미만일 때 플레이스홀더 추가
            if (currentPage === Math.ceil(category.cntRow / itemsPerPage) && itemsInCurrentPage < 4 && itemsInCurrentPage > 0) {
              const neededItems = 4 - itemsInCurrentPage;
              
              for (let i = 0; i < neededItems; i++) {
                const placeholder = document.createElement('div');
                placeholder.className = 'grid-placeholder';
                productContainer.appendChild(placeholder);
              }
            }
          }
          
          // 터치 이벤트 핸들러 추가
          const touchStartHandler = (e) => handleTouchStart(e, category.id);
          const touchEndHandler = (e) => handleTouchEnd(e, category.id);
          
          productContainer.addEventListener('touchstart', touchStartHandler, { passive: true });
          productContainer.addEventListener('touchend', touchEndHandler, { passive: true });
          
          scrollHandlers.push({ 
            container: productContainer, 
            touchStartHandler,
            touchEndHandler
          });
        }
      });
    }, 100);
    
    return () => {
      clearTimeout(timer);
      scrollHandlers.forEach(({ container, touchStartHandler, touchEndHandler }) => {
        if (touchStartHandler) container.removeEventListener('touchstart', touchStartHandler);
        if (touchEndHandler) container.removeEventListener('touchend', touchEndHandler);
      });
    };
  }, [categories, isMobile, handleTouchStart, handleTouchEnd, categoryPages]);

  // 페이지네이션 핸들러 수정
  const handlePageChange = (categoryId, page) => {
    
    // 페이지 상태 업데이트
    setCategoryPages(prev => ({
      ...prev,
      [categoryId]: page
    }));
    
    // API 호출
    fetchCategoryData(categoryId, page, true);
    
    // 모바일에서 스크롤 위치 조정
    if (isMobile) {
      setTimeout(() => {
        const productContainer = document.querySelector(`[data-category-id="${categoryId}"] .products-grid`);
        if (productContainer) {
          const scrollPosition = (page - 1) * productContainer.clientWidth;
          productContainer.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // 카테고리용 모바일 스와이퍼 옵션 추가 (2행 x 2열 = 4개)
  const getCategoryMobileSwiperOptions = (sectionId) => ({
    slidesPerView: 2,
    spaceBetween: 8,
    autoHeight: false,
    slidesPerGroup: 4,
    grid: {
      rows: 2,
      fill: 'row'
    },
    modules: [
      require('swiper/modules').Pagination, 
      require('swiper/modules').Grid,
      require('swiper/modules').Navigation
    ],
    pagination: { 
      clickable: true,
    },
    navigation: {
      nextEl: `#${sectionId}-next`,
      prevEl: `#${sectionId}-prev`,
    },
    loop: false,
    centeredSlides: false,
    allowTouchMove: true,
    breakpoints: {
      480: {
        slidesPerView: 2,
        spaceBetween: 8,
        slidesPerGroup: 4,
        grid: {
          rows: 2,
          fill: 'row'
        }
      }
    }
  });

  return(
    <div ref={pageRef}>
      <section className="prod_section">
        <div 
          className="product_banner cate_brand"
          style={{backgroundImage:`url(${window.innerWidth > 768 ? categoryInfo?.pc_img_url : categoryInfo?.mobile_img_url})`}}
        >
          <div className="inner">
            <ul className="bread_crumb">
              <li className="group_item" style={{cursor: 'pointer'}} onClick={() => handleBreadcrumbClick('home')}>
                <p>HOME</p>
              </li>
              <li className="group_item active">
                <p>{title}</p>
              </li>
            </ul>
            
            <div className="product_tit">
              <h4 className="tit">{title}</h4>
              <p className="sub">{categoryInfo?.cate_comment}</p>
            </div>

            {categoryNames.length > 0 && title !== "BRAND" && title !== "PURPOSE" && (
              <CategoryTab 
                categoryNames={categoryNames} 
                mainCategory={title}
              />
            )}
          </div>
        </div>

        <div className="inner main_prod_list cate_container gap_64">
          
          {bestProducts.length > 0 && (
            <div>
              <div className="brand_top_tab">
                <div className="brand_section_title">
                  BEST ITEM
                </div>
              </div>

              <div className="p0">
                {loading ? (
                  <div>로딩 중...</div>
                ) : (
                  <div className="swiper_parent" style={{ position: 'relative' }}>
                    <ProductSection
                      products={bestProducts}
                      showTabs={false}
                      showMoreButton={false}
                      useSwiper={isMobile || (!isMobile && bestProducts.length > 5)} // 모바일이거나 PC에서 5개 초과일 때
                      swiperOptions={
                        isMobile 
                          ? getMobileSwiperOptions('best-products')
                          : (!isMobile && bestProducts.length > 5)
                          ? getPCSwiperOptions('best-products')
                          : undefined
                      }
                      sectionId="best-products"
                    />
                    
                    {/* 네비게이션 버튼: 모바일이거나 PC에서 5개 초과일 때만 */}
                    {(isMobile) && (
                      <>
                        <div 
                          id="best-products-prev" 
                          className="swiper-button-prev best-products-nav"
                          style={{
                            position: 'absolute',
                            top: '47%',
                            left: isMobile ? '10px' : '-60px',
                            transform: 'translateY(-50%)',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        />
                        <div 
                          id="best-products-next" 
                          className="swiper-button-next best-products-nav"
                          style={{
                            position: 'absolute',
                            top: '47%',
                            right: isMobile ? '10px' : '-60px',
                            transform: 'translateY(-50%)',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 카테고리별 섹션을 map으로 렌더링 */}
          {loading ? (
            <div>카테고리 로딩 중...</div>
          ) : (
            categories.map(category => (
              category.data && category.data.length > 0 && (
                <div key={category.id}>
                  <div className="brand_top_tab">
                    <div className="brand_section_title">
                      {category.category_name}
                    </div>
                  </div>

                  <div className="p0 cate_cinema_con" data-category-id={category.id}>
                    <CategoryBeforeContent cntRow={category.cntRow} category={category} />
                    
                    {/* 모바일: 스와이퍼, PC: 일반 그리드 */}
                    {isMobile ? (
                      <div className="swiper_parent" style={{ position: 'relative' }}>
                        <ProductSection
                          products={category.data}
                          showTabs={false}
                          showMoreButton={false}
                          useSwiper={true}
                          swiperOptions={getCategoryMobileSwiperOptions(`category-${category.id}`)}
                          sectionId={`category-${category.id}`}
                          packageFlag={category.package_flag}
                        />
                        
                        {/* 네비게이션 버튼 */}
                        <div 
                          id={`category-${category.id}-prev`}
                          className="swiper-button-prev"
                          style={{
                            position: 'absolute',
                            top: '47%',
                            left: '10px',
                            transform: 'translateY(-50%)',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        />
                        <div 
                          id={`category-${category.id}-next`}
                          className="swiper-button-next"
                          style={{
                            position: 'absolute',
                            top: '47%',
                            right: '10px',
                            transform: 'translateY(-50%)',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <ProductSection
                          products={category.data}
                          showTabs={false}
                          showMoreButton={false}
                          useSwiper={false}
                          packageFlag={category.package_flag}
                        />

                        {/* PC에서만 페이지네이션 표시 */}
                        <div className="pagination_wrapper" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                          <PaginationSection
                            totalCount={parseInt(category.cntRow)}
                            itemsPerPage={getItemsPerPage()}
                            currentPage={categoryPages[category.id] || 1}
                            onPageChange={(page) => handlePageChange(category.id, page)}
                            onArrow={(page) => handlePageChange(category.id, page)}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )
            ))
          )}
        </div>
      </section>
    </div>
  )
};

export default CinemaPage;